import React from "react";

/* PacSana's "24-Hour Activity" clock: one day drawn as two concentric rings —
   where the bracelet was (outer) and when it was moving (inner) — laid out on a
   clock face with noon at the top, the way PacSana's own dashboard draws it.

   Recharts has no radial-timeline mark, so this is hand-drawn SVG. */

const SIZE = 320;
const CENTER = SIZE / 2;
// Two wide rings with the clock face in the gap between them, as PacSana draws
// it: presence (where the bracelet was) outside, movement inside.
// Radii are in the proportions PacSana uses: a wide presence ring at the rim, a
// smaller movement ring well inside it, and a broad clock face between the two
// with room for the hour labels and the sun/moon marks.
const PRESENCE_R = 137;
const PRESENCE_W = 22;
const ACTIVITY_R = 62;
const ACTIVITY_W = 26;
// The face sits in the gap between the rings with room to breathe on both
// sides: roughly 8px from the movement ring out to the labels and marks, and
// again from those out to the ticks.
const FACE_R = 114;   // the dotted circle the ticks hang from
const LABEL_R = 91;   // hour labels, clear of the movement ring below the ticks
const MARK_R = 94;    // sun and moon marks, on the diagonals between the labels

// Taken from PacSana's own 24-hour panel so the two read as the same picture:
// green movement inside, light blue living area and violet bed area outside,
// pale grey wherever the bracelet wasn't detected. Not tab accents — the legend
// names each one and they mean the same thing on every day, so they stay fixed.
export const DIAL_COLORS = {
  activity: "#22b573",
  sleep: "#3f8ae0",
  living: "#a5dbf3",
  bed: "#8b7ff2",
  out: "#dfe3e8",
};

const KIND_WORDS = {
  activity: ["activity", "active", "steps", "step", "walking", "walk", "moving", "movement"],
  sleep: ["sleep", "sleeping", "asleep"],
  bed: ["bed", "bedarea", "bedroom"],
  living: ["living", "livingarea", "home", "inrange", "present", "lounge", "indoors"],
  out: ["out", "outofrange", "outrange", "absent", "away", "outside", "offline", "unknown", "nosignal"],
};

const normaliseWord = (value) =>
  String(value || "").toLowerCase().replace(/[^a-z]/g, "");

// PacSana labels its states in several ways across feeds ("living_area",
// "livingArea", "LIVING AREA"), so states are matched on letters alone.
const kindOf = (raw) => {
  const word = normaliseWord(raw);
  if (!word) return null;
  const entry = Object.entries(KIND_WORDS).find(([, words]) =>
    words.some((candidate) => word === candidate || word.startsWith(candidate)),
  );
  return entry ? entry[0] : null;
};

// Accepts hours (15.5), clock strings ("15:20") and timestamps, all of which
// turn up in PacSana payloads depending on the endpoint.
const toHours = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const text = String(value).trim();
  const clock = text.match(/^(\d{1,2}):(\d{2})/);
  if (clock) return Number(clock[1]) + Number(clock[2]) / 60;

  // Read in UTC, not local time. PacSana stamps each reading against the
  // calendar day it was asked for, so reading it in the viewer's zone would
  // rotate the whole dial for anyone not sitting on UTC.
  const stamp = new Date(text);
  if (!Number.isNaN(stamp.getTime())) {
    return stamp.getUTCHours() + stamp.getUTCMinutes() / 60 + stamp.getUTCSeconds() / 3600;
  }

  const num = Number(text);
  return Number.isFinite(num) ? num : null;
};

// Where the outer ring gets its state. `fallback` covers the raw feed, where a
// location is a PacSana zone name that varies per site — anything named that
// isn't the bed area means the bracelet was seen somewhere in the home.
const LOCATION_KINDS = [
  ["bed", "bed"],
  ["living", "living"],
  ["out", "out"],
  ["norange", "out"],
];

const locationKind = (value, fallback = null) => {
  const word = normaliseWord(value);
  if (!word) return null;
  const hit = LOCATION_KINDS.find(([prefix]) => word.startsWith(prefix));
  return hit ? hit[1] : fallback;
};

// Only movement reaches the inner ring — "resting" is the pale track behind it —
// and out-of-range is carried by the location ring, so it isn't drawn twice.
const activityKind = (value) =>
  normaliseWord(value).startsWith("activ") ? "activity" : null;

const sleepKind = () => "sleep";

// `resolve` names the dimension being read: without one the state is matched
// against every kind at once, which is what a flat list of segments needs.
const stateOf = (raw, resolve) => {
  const value = raw.kind ?? raw.state ?? raw.type ?? raw.zone ?? raw.area ?? raw.status;
  return resolve ? resolve(value) : kindOf(value);
};

const rawStart = (raw) =>
  raw.start ?? raw.from ?? raw.startTime ?? raw.startsAt ?? raw.begin ?? raw.time ?? raw.timestamp;

const toSegment = (raw, resolve) => {
  if (!raw || typeof raw !== "object") return null;
  const start = toHours(rawStart(raw));
  const end = toHours(raw.end ?? raw.to ?? raw.endTime ?? raw.endsAt ?? raw.finish);
  const kind = stateOf(raw, resolve);
  if (start === null || end === null || !kind) return null;
  return { start: Math.max(0, start), end: Math.min(24, end <= start ? 24 : end), kind };
};

// Point samples — one row per minute or per 5-minute epoch, with a time but no
// end — collapsed into the runs the dial draws. Each sample holds until the next
// one; the last holds to midnight.
const runsBy = (samples, pick) => {
  const runs = [];
  samples.forEach((sample, index) => {
    const kind = pick(sample);
    const end = index + 1 < samples.length ? samples[index + 1].at : 24;
    if (!kind || end <= sample.at) return;
    const open = runs[runs.length - 1];
    if (open && open.kind === kind && Math.abs(open.end - sample.at) < 0.001) {
      open.end = end;
    } else {
      runs.push({ start: sample.at, end, kind });
    }
  });
  return runs;
};

const runsFromSamples = (list, resolve) => {
  const samples = list
    .map((raw) => ({ at: toHours(rawStart(raw)), kind: stateOf(raw, resolve) }))
    .filter((sample) => sample.at !== null)
    .sort((a, b) => a.at - b.at);
  return runsBy(samples, (sample) => sample.kind);
};

/* ── /api/external/24HourActivityData ──────────────────────────────────
   One row per reading, each carrying the device's own classification code and
   the location it was seen in:

     class 0 = Resting, 1 = Moving, 2 = OutOfRange, 3 = Sleep

   Resting isn't drawn — it is the pale track the movement ring sits on — so
   only Moving and Sleep reach the inner ring. */
const CLASS_ACTIVITY = { 1: "activity", 3: "sleep" };

// The class code decides out-of-range, since it is the one value that is
// definitely an enum; the location name only has to separate bed from the rest.
const presenceOf = ({ cls, location }) =>
  cls === 2 ? "out" : locationKind(location, "living");

const rawDaySegments = (value) => {
  const samples = (value.activities || [])
    .map((row) => ({
      at: toHours(row.date ?? row.time ?? row.timestamp),
      cls: Number(row.class ?? row.activityClass ?? row.classification),
      location: row.locationName ?? row.location ?? row.zone,
    }))
    .filter((sample) => sample.at !== null)
    .sort((a, b) => a.at - b.at);

  const segments = [
    ...runsBy(samples, presenceOf),
    ...runsBy(samples, ({ cls }) => CLASS_ACTIVITY[cls] || null),
  ];

  // Sleep sessions arrive as intervals already, and are truer than the
  // per-sample class, so they stand in for the class-3 runs where present.
  const sleep = (value.sleepSessions || [])
    .map((session) => toSegment(session, sleepKind))
    .filter(Boolean);

  return sleep.length
    ? [...segments.filter((segment) => segment.kind !== "sleep"), ...sleep]
    : segments;
};

const hasList = (value, keys) =>
  !!value && typeof value === "object" && keys.some((key) => Array.isArray(value[key]));

// PacSana's own per-sample payload, as opposed to a day already shaped into
// interval lists.
const isRawDay = (value) => hasList(value, ["activities", "sleepSessions"]);

// One day's payload, in any of the shapes a day has arrived in.
const isDayPayload = (value) =>
  hasList(value, ["activities", "sleepSessions", "activity", "location", "segments"]);

// The calendar day a raw payload belongs to, when it isn't already keyed by one.
const dayKeyOf = (value) => {
  const stamp =
    value.date ||
    value.day ||
    value.activities?.[0]?.date ||
    value.sleepSessions?.[0]?.from;
  return stamp ? String(stamp).slice(0, 10) : null;
};

const readList = (list, resolve) => {
  if (!Array.isArray(list) || !list.length) return [];
  const intervals = list.map((raw) => toSegment(raw, resolve)).filter(Boolean);
  return intervals.length ? intervals : runsFromSamples(list, resolve);
};

// A day arrives as the raw `{ activities, sleepSessions }` payload from
// 24HourActivityData, as one flat list of segments each carrying its own state,
// or as an `{ activity, location }` pair of interval lists.
const toSegments = (value) => {
  if (Array.isArray(value)) return readList(value);
  if (!value || typeof value !== "object") return [];
  if (isRawDay(value)) return rawDaySegments(value);
  if (Array.isArray(value.segments)) return readList(value.segments);
  return [
    ...readList(value.activity, activityKind),
    ...readList(value.location, locationKind),
  ];
};

/**
 * Pulls the 24-hour activity feed out of the PacSana summary payload.
 *
 * The feed is optional and has moved between shapes, so several are read: a
 * `dayActivity` map keyed by date, a list of days, a single raw
 * 24HourActivityData payload, or a reading whose points carry their own
 * segments. Anything unrecognised gives an empty map and the panel says it has
 * nothing for that day rather than throwing.
 *
 * Returns { days: ISO date strings ascending, byDay: { [iso]: segments } }.
 */
export const extractDayActivity = (data) => {
  const source =
    data?.dayActivity ??
    data?.dailyActivity ??
    data?.activity24h ??
    data?.activityHistory ??
    data?.readings?.activity_24h ??
    data?.readings?.day_activity ??
    // A single day's payload, whether our backend shaped it or passed PacSana's
    // through untouched.
    (isDayPayload(data) ? data : null);

  const byDay = {};

  const add = (date, value) => {
    const day = date || (value && typeof value === "object" ? dayKeyOf(value) : null);
    if (!day || !value) return;
    const segments = toSegments(value);
    if (segments.length) byDay[String(day).slice(0, 10)] = segments;
  };

  const list = Array.isArray(source) ? source : source?.days || source?.points;

  if (isDayPayload(source)) {
    add(null, source);
  } else if (list) {
    list.forEach((entry) => add(null, entry));
  } else if (source && typeof source === "object") {
    Object.entries(source).forEach(([date, value]) => add(date, value));
  }

  return { days: Object.keys(byDay).sort(), byDay };
};

/** Hours in each ring category for one day's segments. */
export const dayActivityTotals = (segments = []) =>
  segments.reduce((totals, segment) => {
    totals[segment.kind] = (totals[segment.kind] || 0) + Math.max(0, segment.end - segment.start);
    return totals;
  }, {});

// Noon at the top, clockwise — so 6am sits left, 6pm right and midnight bottom.
const hourToAngle = (hour) => (((hour - 12) / 24) * 360 + 360) % 360;

const polar = (radius, angle) => {
  const rad = ((angle - 90) * Math.PI) / 180;
  return {
    x: CENTER + radius * Math.cos(rad),
    y: CENTER + radius * Math.sin(rad),
  };
};

const arcPath = (radius, startAngle, endAngle) => {
  const start = polar(radius, startAngle);
  const end = polar(radius, endAngle);
  const large = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${radius} ${radius} 0 ${large} 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
};

// A segment that runs through noon wraps past the top of the dial, which one arc
// can't express — those are cut into two.
const segmentArcs = (segment) => {
  const from = (segment.start - 12 + 24) % 24;
  const to = (segment.end - 12 + 24) % 24;
  const spans = to > from ? [[from, to]] : [[from, 24], [0, to]];
  return spans
    .filter(([a, b]) => b - a > 0.004)
    .map(([a, b]) => [(a / 24) * 360, (b / 24) * 360]);
};

const Ring = ({ segments, radius, width, kinds }) => (
  <>
    {segments
      .filter((segment) => kinds.includes(segment.kind))
      .flatMap((segment, index) =>
        segmentArcs(segment).map((span, part) =>
          span[1] - span[0] >= 359.9 ? (
            <circle
              key={`${index}-${part}`}
              cx={CENTER}
              cy={CENTER}
              r={radius}
              fill="none"
              stroke={DIAL_COLORS[segment.kind]}
              strokeWidth={width}
            />
          ) : (
            <path
              key={`${index}-${part}`}
              d={arcPath(radius, span[0], span[1])}
              fill="none"
              stroke={DIAL_COLORS[segment.kind]}
              strokeWidth={width}
              strokeLinecap="butt"
            />
          ),
        ),
      )}
  </>
);

const HOUR_LABELS = [
  { hour: 12, text: "12", sub: "pm" },
  { hour: 18, text: "6", sub: "pm" },
  { hour: 0, text: "12", sub: "am" },
  { hour: 6, text: "6", sub: "am" },
];

/* The dotted ring threads the face together, running between each hour label and
   the sun or moon next to it. It is drawn as eight arcs rather than one circle,
   so the dots stop short of what they connect instead of running through it —
   the gap is wider either side of a two-line label than a 20px glyph. */
const FACE_ANCHORS = [
  { hour: 0, gap: 13 },
  { hour: 3, gap: 8 },
  { hour: 6, gap: 13 },
  { hour: 9, gap: 8 },
  { hour: 12, gap: 13 },
  { hour: 15, gap: 8 },
  { hour: 18, gap: 13 },
  { hour: 21, gap: 8 },
];

// The quarter marks between the hour labels, as on PacSana's face: dawn at 9am,
// midday sun at 3pm, and the small hours either side of midnight.
const FACE_MARKS = [
  { hour: 3, shape: "moon" },
  { hour: 9, shape: "sunrise" },
  { hour: 15, shape: "sun" },
  { hour: 21, shape: "moon" },
];

const MARK_SIZE = 20;

const SUN_RAYS = Array.from({ length: 8 }, (_, i) => {
  const angle = (i * Math.PI) / 4;
  return {
    x1: 12 + 7.3 * Math.cos(angle),
    y1: 12 + 7.3 * Math.sin(angle),
    x2: 12 + 10.4 * Math.cos(angle),
    y2: 12 + 10.4 * Math.sin(angle),
  };
});

/* Filled rather than outlined. These sit at 18px, and at that size a 1.5px
   outline — a lucide Sunrise especially — collapses into a scribble; a solid
   disc, crescent and half-sun keep their shape at this size. */
const FaceMark = ({ shape, x, y }) => (
  <svg
    x={x}
    y={y}
    width={MARK_SIZE}
    height={MARK_SIZE}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    className="pacsana-dial-mark"
    aria-hidden="true"
  >
    {shape === "moon" ? (
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" fill="currentColor" stroke="none" />
    ) : null}

    {shape === "sun" ? (
      <>
        <circle cx="12" cy="12" r="4.4" fill="currentColor" stroke="none" />
        <g strokeWidth={2.2}>
          {SUN_RAYS.map((ray) => (
            <line key={`${ray.x1}-${ray.y1}`} {...ray} />
          ))}
        </g>
      </>
    ) : null}

    {shape === "sunrise" ? (
      <>
        <path d="M7.4 18.4 A4.6 4.6 0 0 1 16.6 18.4 Z" fill="currentColor" stroke="none" />
        <g strokeWidth={2.2}>
          <path d="M3.4 18.4 H20.6" />
          <path d="M12 3.2 V6.4" />
          <path d="M5.6 6.2 L7.8 8.4" />
          <path d="M18.4 6.2 L16.2 8.4" />
        </g>
      </>
    ) : null}
  </svg>
);

/* The walking figure at the centre, drawn rather than borrowed: lucide's
   standing figure reads as a person at rest, and this panel is about movement.
   Mid-stride, matching the glyph on PacSana's own dial. */
const WalkingFigure = () => (
  <svg
    x={CENTER - 23}
    y={CENTER - 23}
    width={46}
    height={46}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={3}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="pacsana-dial-figure"
    aria-hidden="true"
  >
    {/* Limbs stay straight: bending them at this size reads as a tangle rather
        than a stride. */}
    <circle cx="12" cy="3.6" r="2.5" fill="currentColor" stroke="none" />
    <path d="M12 6.8 L11.6 13.2" />
    <path d="M11.6 13.2 L14.6 21.2" />
    <path d="M11.6 13.2 L8.2 21.2" />
    <path d="M11.8 8.4 L15.2 12.8" />
    <path d="M11.8 8.4 L8.4 12.6" />
  </svg>
);

const PacSanaDayDial = ({ segments = [] }) => {
  const hasData = segments.length > 0;

  return (
    <div className="pacsana-dial">
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="pacsana-dial-svg" role="img"
        aria-label="24 hour activity clock">
        {/* Tracks: an unrecorded stretch reads as out of range, which is what an
            undetected bracelet means. */}
        <circle cx={CENTER} cy={CENTER} r={PRESENCE_R} fill="none"
          stroke={DIAL_COLORS.out} strokeWidth={PRESENCE_W} opacity={hasData ? 1 : 0.5} />
        <circle cx={CENTER} cy={CENTER} r={ACTIVITY_R} fill="none"
          stroke="#f2f5f8" strokeWidth={ACTIVITY_W} />

        <Ring segments={segments} radius={PRESENCE_R} width={PRESENCE_W} kinds={["living", "bed", "out"]} />
        <Ring segments={segments} radius={ACTIVITY_R} width={ACTIVITY_W} kinds={["sleep", "activity"]} />

        {/* The dotted ring, linking each hour label to the mark beside it. */}
        {FACE_ANCHORS.map((anchor, index) => {
          const next = FACE_ANCHORS[(index + 1) % FACE_ANCHORS.length];
          const from = hourToAngle(anchor.hour) + anchor.gap;
          const to = hourToAngle(next.hour) - next.gap;
          return (
            <path
              key={anchor.hour}
              d={arcPath(MARK_R, from, to > from ? to : to + 360)}
              fill="none"
              stroke="#cdd4e0"
              strokeWidth={1.4}
              strokeDasharray="1 4"
              strokeLinecap="round"
            />
          );
        })}

        {Array.from({ length: 24 }, (_, hour) => {
          const angle = hourToAngle(hour);
          const major = hour % 6 === 0;
          const from = polar(FACE_R + (major ? -4 : 0), angle);
          const to = polar(FACE_R + 6, angle);
          return (
            <line key={hour} x1={from.x} y1={from.y} x2={to.x} y2={to.y}
              stroke="#c7cede" strokeWidth={major ? 1.8 : 1.1} strokeLinecap="round" />
          );
        })}

        {HOUR_LABELS.map(({ hour, text, sub }) => {
          const at = polar(LABEL_R, hourToAngle(hour));
          return (
            <text key={`${text}${sub}`} x={at.x} y={at.y - 4} textAnchor="middle"
              className="pacsana-dial-hour">
              <tspan x={at.x} dy="0">{text}</tspan>
              <tspan x={at.x} dy="10">{sub}</tspan>
            </text>
          );
        })}

        {FACE_MARKS.map(({ hour, shape }) => {
          const at = polar(MARK_R, hourToAngle(hour));
          return (
            <FaceMark key={hour} shape={shape}
              x={at.x - MARK_SIZE / 2} y={at.y - MARK_SIZE / 2} />
          );
        })}

        <WalkingFigure />
      </svg>
    </div>
  );
};

export default PacSanaDayDial;
